<!-- Loading Skeleton -->
<div *ngIf="isInventoryLoading || isCategoryLoading || isOrderDetailsLoading" class="inventory-skeleton" @fadeInUp>
  <div class="container">
    <!-- Header Skeleton -->
    <div class="skeleton-header">
      <div class="skeleton-title"></div>
      <div class="skeleton-cart-icon"></div>
    </div>

    <!-- Search Skeleton -->
    <div class="skeleton-search">
      <div class="skeleton-search-bar"></div>
    </div>

    <!-- Products Skeleton -->
    <div class="skeleton-products">
      <div class="skeleton-products-grid">
        <div *ngFor="let skeleton of skeletonItems; trackBy: trackByItem" class="skeleton-product-item">
          <div class="skeleton-product-image"></div>
          <div class="skeleton-product-name"></div>
          <div class="skeleton-product-price"></div>
          <div class="skeleton-product-button"></div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Main Content -->
<div *ngIf="!isInventoryLoading && !isCategoryLoading && !isOrderDetailsLoading" class="inventory-container" @fadeInUp>
  <div class="container">

    <!-- Header Section -->
    <div class="header-section" @slideDown>
      <div class="header-content">
        <div class="header-info">
          <div class="category-badge">
            <i class="fa fa-utensils"></i>
            <span>Category</span>
          </div>
          <h1 class="category-title">
            {{ category && capitalizeFirstLetter(category.name) }}
            <span class="items-count" *ngIf="filteredItems.length > 0">
              {{ filteredItems.length }} {{ filteredItems.length === 1 ? 'Item' : 'Items' }}
            </span>
          </h1>
        </div>
        <div class="cart-section">
          <button class="cart-btn" (click)="cart(content2)" type="button">
            <div class="cart-icon-wrapper">
              <i class="fa fa-shopping-cart"></i>
            </div>
            <span class="cart-text">View Cart</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Search Section -->
    <div class="search-section" @slideDown>
      <div class="search-card">
        <div class="search-header">
          <i class="fa fa-search search-icon"></i>
          <h3>Search Products</h3>
        </div>
        <div class="search-wrapper">
          <input
            type="text"
            [(ngModel)]="searchTerm"
            (input)="onSearch($event)"
            placeholder="Search in {{ category?.name }}"
            class="search-input"
            [disabled]="isSearchLoading">
          <div class="search-actions">
            <button class="search-btn" type="button" *ngIf="!isSearchLoading">
              <i class="fa fa-search"></i>
            </button>
            <div class="search-loading" *ngIf="isSearchLoading">
              <div class="search-spinner"></div>
            </div>
            <button class="clear-btn" type="button" *ngIf="searchTerm && !isSearchLoading" (click)="searchTerm = ''; onSearch({target: {value: ''}})">
              <i class="fa fa-times"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div *ngIf="filteredItems.length === 0 && !isInventoryLoading" class="empty-state" @fadeInUp>
      <div class="empty-state-content">
        <div class="empty-state-icon">
          <i class="fa fa-utensils"></i>
        </div>
        <div class="empty-state-text">
          <h4 *ngIf="searchTerm; else noItemsTitle">No results found</h4>
          <ng-template #noItemsTitle>
            <h4>No items available</h4>
          </ng-template>

          <p *ngIf="searchTerm; else noItemsMessage">
            No results found for "<strong>{{ searchTerm }}</strong>" in {{ category?.name }}
          </p>
          <ng-template #noItemsMessage>
            <p>Sorry, no items are available in {{ category?.name }} menu right now. Please explore other categories.</p>
          </ng-template>
        </div>
        <div class="empty-state-actions">
          <a [routerLink]="['/home']" class="btn btn-primary btn-lg">
            <i class="fa fa-arrow-left me-2"></i>
            Explore Other Categories
          </a>
        </div>
      </div>
    </div>

    <!-- Products Grid -->
    <div *ngIf="filteredItems.length > 0" class="products-section" @staggerAnimation>
      <div class="products-grid">
        <div *ngFor="let item of filteredItems; trackBy: trackByItem" class="product-card" @fadeInUp>
          <div class="product-image-wrapper">
            <img
              [src]="item.main_image"
              [alt]="item.name"
              class="product-image"
              (error)="onImageError($event)"
              loading="lazy">
            <div class="product-overlay" *ngIf="item.quantity === 0">
              <span class="out-of-stock-badge">Out of Stock</span>
            </div>
          </div>

          <div class="product-info">
            <h6 class="product-name">{{ item.name }}</h6>
            <div class="product-price">
              <span class="price">{{ item.selling_price | currency: '₦' }}</span>
              <span class="price-unit">per unit</span>
            </div>
          </div>

          <div class="product-actions">
            <!-- In Cart Controls -->
            <div *ngIf="item.inCart; else addToCartSection" class="quantity-controls">
              <button
                class="quantity-btn quantity-decrease"
                [disabled]="item.userQuantity < 1"
                (click)="decrement(item)"
                type="button">
                <i class="fa fa-minus"></i>
              </button>
              <div class="quantity-display">
                <span class="quantity-text">{{ item.userQuantity }}</span>
              </div>
              <button
                class="quantity-btn quantity-increase"
                (click)="increment(item)"
                type="button">
                <i class="fa fa-plus"></i>
              </button>
            </div>

            <!-- Add to Cart Section -->
            <ng-template #addToCartSection>
              <div *ngIf="item.quantity > 0; else outOfStockSection">
                <button
                  class="btn btn-primary btn-add-to-cart"
                  [disabled]="noTable"
                  [class.loading]="item.isAddingToCart"
                  (click)="addToCartModal(item, content3)"
                  type="button">
                  <div class="btn-spinner" *ngIf="item.isAddingToCart"></div>
                  <i class="fa fa-shopping-cart" *ngIf="!item.isAddingToCart"></i>
                  <span class="btn-text" *ngIf="!item.isAddingToCart">Add To Cart</span>
                </button>
              </div>

              <ng-template #outOfStockSection>
                <div class="out-of-stock-info">
                  <i class="fa fa-exclamation-triangle"></i>
                  <span class="out-of-stock-text">Out of Stock</span>
                </div>
              </ng-template>
            </ng-template>
          </div>
        </div>
      </div>
    </div>

  </div>
</div>


<!-- Cart Modal -->
<ng-template #content2 let-modal>
  <div class="custom-modal">
    <div class="modal-header">
      <div class="modal-title">
        <i class="fa fa-shopping-cart"></i>
        <h3>Your Cart</h3>
      </div>
      <button type="button" class="modal-close-btn" (click)="modal.close('Cross click')">
        <i class="fa fa-times"></i>
      </button>
    </div>
    <div class="modal-content">
      <app-cart [orderId]="orderId"></app-cart>
    </div>
  </div>
</ng-template>

<!-- Add to Cart Modal -->
<ng-template #content3 let-modal>
  <div class="custom-modal">
    <div class="modal-header">
      <div class="modal-title">
        <i class="fa fa-plus-circle"></i>
        <h3>Add to Cart</h3>
      </div>
      <button type="button" class="modal-close-btn" (click)="modal.dismiss('Cross click')">
        <i class="fa fa-times"></i>
      </button>
    </div>
    <div class="modal-content">
      <div class="add-to-cart-content">
        <div class="variation-header">
          <h6 class="variation-title">Please select a variation</h6>
        </div>

        <div class="product-item-modal">
          <div class="product-details">
            <div class="product-modal-info">
              <h6 class="product-modal-name">{{ tempItem?.name }}</h6>
              <div class="product-modal-price">
                <span class="price">{{ tempItem?.selling_price | currency: '₦' }}</span>
                <span class="price-unit">per unit</span>
              </div>
            </div>
            <div class="quantity-controls-modal">
              <button
                class="quantity-btn quantity-decrease"
                [disabled]="tempItem?.userQuantity < 1"
                (click)="decrement(tempItem)"
                type="button">
                <i class="fa fa-minus"></i>
              </button>
              <div class="quantity-display">
                <span class="quantity-text">{{ tempItem?.userQuantity }}</span>
              </div>
              <button
                class="quantity-btn quantity-increase"
                (click)="increment(tempItem)"
                type="button">
                <i class="fa fa-plus"></i>
              </button>
            </div>
          </div>
        </div>

        <hr class="modal-divider" />

        <div class="modal-actions">
          <button
            class="btn btn-secondary btn-lg continue-shopping"
            (click)="modal.dismiss()"
            type="button">
            <i class="fa fa-arrow-left me-2"></i>
            Continue Shopping
          </button>
          <button
            class="btn btn-primary btn-lg view-cart"
            (click)="cart(content2)"
            type="button">
            <i class="fa fa-shopping-cart me-2"></i>
            View Cart & Checkout
          </button>
        </div>
      </div>
    </div>
  </div>
</ng-template>
